import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { environmentPath } from '../../utils/paths';
import './Breadcrumbs.scss';

export interface IBreadcrumb {
    label: string;
    /** Where the crumb leads. Left out for the current page, and for a level that has no page of its own. */
    to?: string;
}

/**
 * Where a page sits under the dashboard, one level per crumb.
 *
 * Replaces the separate "back to ..." links the pages each had: those only led one level up, and every page
 * phrased and placed its own. The last crumb is the page itself and is never a link.
 */
export const Breadcrumbs = ({ items }: { items: IBreadcrumb[] }) => (
    <nav className="breadcrumbs" aria-label="Breadcrumb">
        <ol className="breadcrumbs__list">
            {items.map((item, index) => {
                const current = index === items.length - 1;

                return (
                    <Fragment key={`${index}-${item.label}`}>
                        {index > 0 && (
                            <li className="breadcrumbs__separator" aria-hidden>
                                <FontAwesomeIcon icon="angle-right" />
                            </li>
                        )}
                        <li className="breadcrumbs__item">
                            {item.to && !current ? (
                                <Link
                                    to={item.to}
                                    className="breadcrumbs__link"
                                >
                                    {item.label}
                                </Link>
                            ) : (
                                <span
                                    className={
                                        current
                                            ? 'breadcrumbs__current'
                                            : undefined
                                    }
                                    aria-current={current ? 'page' : undefined}
                                >
                                    {item.label}
                                </span>
                            )}
                        </li>
                    </Fragment>
                );
            })}
        </ol>
    </nav>
);

/** The crumbs every page below an environment starts with. */
export const environmentCrumbs = (
    customerID?: string,
    environment?: string
): IBreadcrumb[] => [
    { label: 'Dashboard', to: '/dashboard' },
    // There is no page per customer, so the customer is a level to read rather than to click.
    { label: customerID ?? '' },
    { label: environment ?? '', to: environmentPath(customerID, environment) },
];
